import { generateNKeysBetween } from 'fractional-indexing';
import { union, rectOf } from './geometry';
import type { CardItem, CardStatus, ConnectorItem, FrameItem, Item, MindNodeItem, Point, ShapeItem, StickyItem, TextItem } from './types';
import { nanoid } from './id';
import { CARD_H, CARD_W } from './cards';

export type TemplateId = 'brainstorm' | 'retro' | 'kanban' | 'mindmap' | 'swot' | 'journey';

export const TEMPLATE_IDS: TemplateId[] = ['brainstorm', 'retro', 'kanban', 'mindmap', 'swot', 'journey'];

export interface TemplateLabels {
  title: Record<TemplateId, string>;
  brainstormTopic: string;
  retroGood: string;
  retroBad: string;
  retroActions: string;
  retroTitle: string;
  backlog: string;
  todo: string;
  doing: string;
  done: string;
  task: string;
  centralTopic: string;
  branch: string;
  idea: string;
  strengths: string;
  weaknesses: string;
  opportunities: string;
  threats: string;
  stages: string[];
  actions: string;
  feelings: string;
  pains: string;
  ideas: string;
}

/** Mind map topic; `parentIndex` points at its parent draft. Sizes and positions come from the layout. */
type MindDraft = Omit<MindNodeItem, 'id' | 'z'> & { parentIndex?: number };

type Draft =
  | Omit<StickyItem, 'id' | 'z'>
  | Omit<ShapeItem, 'id' | 'z'>
  | Omit<TextItem, 'id' | 'z'>
  | Omit<FrameItem, 'id' | 'z'>
  | Omit<CardItem, 'id' | 'z'>
  | (Omit<ConnectorItem, 'id' | 'z'> & { key?: string })
  | MindDraft;

const card = (x: number, y: number, text: string, status: CardStatus, color: string): Omit<CardItem, 'id' | 'z'> => ({
  type: 'card',
  x,
  y,
  w: CARD_W,
  h: CARD_H,
  text,
  description: '',
  tags: [],
  status,
  color,
});

const topic = (text: string, parentIndex?: number, index = 0, side?: 'left' | 'right'): MindDraft => ({
  type: 'mindnode',
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  text,
  order: `a${index}`,
  parentIndex,
  side,
});

const sticky = (x: number, y: number, color: string, text = ''): Omit<StickyItem, 'id' | 'z'> => ({
  type: 'sticky',
  x,
  y,
  w: 200,
  h: 200,
  color,
  text,
  size: 0,
  align: 'center',
});

const frame = (x: number, y: number, w: number, h: number, title: string, fill = 'auto'): Omit<FrameItem, 'id' | 'z'> => ({ type: 'frame', x, y, w, h, title, fill });

const text = (x: number, y: number, value: string, size: number, bold = true): Omit<TextItem, 'id' | 'z'> => ({
  type: 'text',
  x,
  y,
  w: value.length * size * 0.62,
  h: size * 1.3,
  text: value,
  size,
  align: 'left',
  bold,
  color: 'ink',
});

const shape = (x: number, y: number, w: number, h: number, value: string, fill: string, textColor = 'ink', kind: ShapeItem['kind'] = 'rounded'): Omit<ShapeItem, 'id' | 'z'> => ({
  type: 'shape',
  kind,
  x,
  y,
  w,
  h,
  fill,
  stroke: fill === 'surface' ? 'ink' : 'transparent',
  strokeWidth: fill === 'surface' ? 2 : 0,
  dash: 'solid',
  text: value,
  textColor,
  size: 0,
  align: 'center',
  bold: true,
});

function layout(id: TemplateId, l: TemplateLabels): { drafts: Draft[]; links: [number, number][] } {
  const drafts: Draft[] = [];
  const links: [number, number][] = [];
  switch (id) {
    case 'brainstorm': {
      drafts.push(frame(0, 0, 1800, 1200, l.title.brainstorm));
      const topic = drafts.push(shape(720, 520, 360, 160, l.brainstormTopic, '#5B4CF0', '#FFFFFF')) - 1;
      const colors = ['#FFE066', '#FFC98B', '#FFB3BA', '#D9C5FF', '#B8D4FF', '#A9EDE6', '#C3EFA6', '#F7B5E4'];
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
        const s = drafts.push(sticky(900 + Math.cos(a) * 640 - 100, 600 + Math.sin(a) * 420 - 100, colors[i])) - 1;
        links.push([topic, s]);
      }
      break;
    }
    case 'retro': {
      drafts.push(text(0, -170, l.retroTitle, 56));
      const cols: [string, string, string][] = [
        [l.retroGood, '#E9F8EF', '#C3EFA6'],
        [l.retroBad, '#FDEBEC', '#FFB3BA'],
        [l.retroActions, '#E8F1FD', '#B8D4FF'],
      ];
      cols.forEach(([title, fill, note], i) => {
        const x = i * 780;
        drafts.push(frame(x, 0, 720, 1100, title, fill));
        drafts.push(sticky(x + 60, 60, note), sticky(x + 300, 60, note), sticky(x + 60, 300, note));
      });
      break;
    }
    case 'kanban': {
      // Columns of task cards; a card's status matches its column.
      const cols: [string, CardStatus, string][] = [
        [l.backlog, 'todo', '#6B7280'],
        [l.todo, 'todo', '#F5A524'],
        [l.doing, 'doing', '#0090FF'],
        [l.done, 'done', '#30A46C'],
      ];
      const colW = CARD_W + 80;
      let n = 1;
      cols.forEach(([title, status, color], i) => {
        const x = i * (colW + 40);
        drafts.push(frame(x, 0, colW, 40 + 4 * (CARD_H + 20) + 20, title));
        const count = [4, 3, 2, 2][i];
        for (let k = 0; k < count; k++) drafts.push(card(x + 40, 40 + k * (CARD_H + 20), `${l.task} ${n++}`, status, color));
      });
      break;
    }
    case 'mindmap': {
      const root = drafts.push(topic(l.centralTopic)) - 1;
      const ideas = [2, 1, 0, 2, 1, 0];
      for (let i = 0; i < 6; i++) {
        const b = drafts.push(topic(`${l.branch} ${i + 1}`, root, i, i < 3 ? 'right' : 'left')) - 1;
        for (let k = 0; k < ideas[i]; k++) drafts.push(topic(`${l.idea} ${k + 1}`, b, k));
      }
      break;
    }
    case 'swot': {
      const q: [string, string, string][] = [
        [l.strengths, '#E9F8EF', '#C3EFA6'],
        [l.weaknesses, '#FDEBEC', '#FFB3BA'],
        [l.opportunities, '#E8F1FD', '#B8D4FF'],
        [l.threats, '#FFF6E5', '#FFC98B'],
      ];
      q.forEach(([title, fill, note], i) => {
        const x = (i % 2) * 980, y = Math.floor(i / 2) * 800;
        drafts.push(frame(x, y, 940, 740, title, fill));
        drafts.push(sticky(x + 60, y + 60, note), sticky(x + 300, y + 60, note));
      });
      break;
    }
    case 'journey': {
      drafts.push(text(0, -180, l.title.journey, 56));
      const rows = [l.actions, l.feelings, l.pains, l.ideas];
      const rowColors = ['#FFE066', '#F7B5E4', '#FFB3BA', '#C3EFA6'];
      l.stages.forEach((stage, i) => {
        drafts.push(shape(320 + i * 300, 0, 260, 90, stage, '#5B4CF0', '#FFFFFF', 'rect'));
      });
      rows.forEach((row, r) => {
        drafts.push(text(0, 150 + r * 260 + 80, row, 26));
        l.stages.forEach((_, i) => drafts.push(sticky(320 + i * 300 + 30, 150 + r * 260, rowColors[r])));
      });
      break;
    }
  }
  return { drafts, links };
}

/** Items for a template, centered on `at`, above everything else in z-order. */
export function buildTemplate(id: TemplateId, at: Point, labels: TemplateLabels, topZ: string, bottomZ: string): Item[] {
  const { drafts, links } = layout(id, labels);
  const nonFrames = drafts.filter((d) => d.type !== 'frame').length + links.length;
  const frames = drafts.filter((d) => d.type === 'frame').length;
  const zs = generateNKeysBetween(topZ, null, nonFrames);
  const fz = frames ? generateNKeysBetween(null, bottomZ, frames) : [];
  let zi = 0, fi = 0;
  const items: Item[] = drafts.map((d) => ({ ...d, id: nanoid(), z: d.type === 'frame' ? fz[fi++] : zs[zi++] }) as Item);
  drafts.forEach((d, i) => {
    if (d.type !== 'mindnode') return;
    const it = items[i] as MindNodeItem & { parentIndex?: number };
    if (d.parentIndex !== undefined) it.parent = items[d.parentIndex].id;
    delete it.parentIndex;
  });
  for (const [a, b] of links) {
    const conn: ConnectorItem = {
      id: nanoid(),
      type: 'connector',
      x: 0,
      y: 0,
      w: 0,
      h: 0,
      z: zs[zi++],
      from: { id: items[a].id, side: 'auto', x: 0, y: 0 },
      to: { id: items[b].id, side: 'auto', x: 0, y: 0 },
      route: id === 'mindmap' ? 'curve' : 'straight',
      stroke: 'ink',
      strokeWidth: 2,
      dash: id === 'brainstorm' ? 'dashed' : 'solid',
      startCap: 'none',
      endCap: id === 'mindmap' ? 'none' : 'none',
      label: '',
    };
    items.push(conn);
  }
  const b = union(items.filter((i) => i.type !== 'connector').map(rectOf))!;
  const dx = at.x - (b.x + b.w / 2), dy = at.y - (b.y + b.h / 2);
  for (const it of items) {
    it.x += dx;
    it.y += dy;
  }
  return items;
}
